import type { z } from "zod";
import { ApiRequestError } from "@/lib/api/api-error";

/**
 * Reads the machine-readable refusal off a failed REST response, for the
 * endpoints whose refusal names what the reader must fix: the caller writes
 * the sentence for its `code` from the catalog. `apiRequest` collapses every
 * non-2xx to one message, so these callers use `apiFetch` and come here.
 *
 * Null for a body carrying no code this build knows (an HTML error page from a
 * proxy, an empty 502, a newer API's code); the caller shows its own generic
 * failure.
 */
export async function readRefusal<T>(
  response: Response,
  schema: z.ZodType<T>
): Promise<T | null> {
  const parsed = schema.safeParse(await response.json().catch(() => null));
  return parsed.success ? parsed.data : null;
}

/** The same, off a refusal `apiRequest` has already thrown. */
export function refusalOf<T>(error: unknown, schema: z.ZodType<T>): T | null {
  if (!(error instanceof ApiRequestError)) {
    return null;
  }
  try {
    const parsed = schema.safeParse(JSON.parse(error.detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
